import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { dispatchService } from '../../../services/central-api/dispatch';
import AssignmentModal from './AssignmentModal';
import MyDataTable from '../../../components/MyDataTable';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import Filter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';

export default function Assignments({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<DispatchParamList, 'Assignments'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['Assignments']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedAssignment, setSelectedAssignment] = useState<Assignment>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getAssignments', filters],
        queryFn: () => dispatchService.getAssignments(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    async function confirmDelete(assignment: Assignment) {
        try {
            await dispatchService.deleteAssignment(assignment.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleFormClose() {
        setSelectedAssignment(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return <Container>
        <Header
            title={translate('assignments')}
            actions={
                <CustomButton
                    text={translate('register')}
                    onPress={() => {
                        setSelectedAssignment(undefined);
                        setModalVisible(true);
                    }}
                />
            }
        />

        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredAssignments')}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter} />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
            />

            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[
                        {
                            name: translate('name'),
                            selector: row => row.name,
                            grow: 5
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.actingBody.name,
                            wrap: true,
                        },
                        {
                            button: true,
                            cell: row =>
                                <DropdownButton
                                    actions={[
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedAssignment(row);
                                                setModalVisible(true);
                                            }
                                        },
                                        {
                                            content: translate('delete'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedAssignment(row);
                                                setDeleteModalVisible(true);
                                            },
                                        }
                                    ]}
                                />
                        }
                    ]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count ?? 0}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />

        </Card>

        <AssignmentModal
            isModalVisible={isModalVisible}
            assignmentId={selectedAssignment?.id}
            onClose={handleFormClose}
        />

        {/* Delete Modal */}
        {selectedAssignment &&
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedAssignment.name}
                onSubmit={async () => {
                    try {
                        if (selectedAssignment) {
                            await confirmDelete(selectedAssignment);
                        }
                        Toast.show({
                            type: 'sentinelxSuccess',
                            text1: translate('ActionSuccessfully'),
                        });
                    } catch (err) {
                        if (err instanceof ClientError) {
                            return Toast.show({
                                type: 'sentinelxError',
                                text1: translate(err.message),
                            });
                        }

                        console.error(err);
                        Toast.show({
                            type: 'sentinelxError',
                            text1: translate('unexpectedError'),
                        });
                    }
                }}
                onCancel={() => {
                    setSelectedAssignment(undefined);
                }}
            />
        }

    </Container >;
}

